// @givmo/social — the page the platform sends the person back to (SMM-WEB1; §SMM-8 point 2, "The
// OAuth return page (the console finishes the leg — no callback exists)").
//
// At `/social/connected`, each console's registered redirect URI. It reads `code` and `state` (or
// the platform's `error`) ONCE, takes them out of the address at once, and posts them to
// `oauth/complete-accounts` ONCE (a module-level spent set, so a remount — React's development
// double-run included — never spends a state twice). Then:
//   * connected at once (one eligible account, or a reconnect): a toast, the accounts read again,
//     and back to the page that asked;
//   * several eligible accounts: the picker, every candidate across the family labelled with its
//     platform (one Meta connect can choose a Page and its Instagram account together), Connect or
//     Cancel (abandon), and its expiry said; the choice is kept in this tab, so a reload can finish it;
//   * refused: the store's code in its own words (`connectRefusalWords`), and the way back.

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  firstErrorCode,
  isGivmoError,
  sdk,
  type OrgIntegrationAccountsCompleteOut,
  type OrgIntegrationCandidateOut,
  type OrgIntegrationPendingGrantOut,
} from '@givmo/api-client';
import { Button, Card, Checkbox, ErrorMessage, testIdProp, toast } from '@givmo/design-system';
import { PlatformMark } from './platform-mark';
import { canManageConnections, type SocialConfig } from './social-config';
import {
  forgetTrip,
  keepPendingGrant,
  readPendingGrant,
  readTrip,
  startConnect,
  type ConnectTrip,
} from './social-connect';
import { platformLabel } from './social-platforms';
import { useConnections, usePendingGrant, useRefreshSocial } from './social-queries';
import { SOCIAL_PAGE_TEST_ID, SOCIAL_ROUTES, socialRedirectUri } from './social-routes';
import { SocialLoading, SocialPageShell, SocialStateCard } from './social-ui';
import { connectRefusalWords, providerDeclinedWords, socialWords as w } from './social-words';

/** The states a return has already spent, across remounts of this page in this tab. */
const SPENT_STATES = new Set<string>();

export interface ReturnParams {
  code: string | null;
  state: string | null;
  error: string | null;
}

/** Read the return's parameters and take them out of the address (§SMM-8: "CLEARS the
 *  parameters"), so a reload, a bookmark or a shared link never carries a code. */
export function takeReturnParams(): ReturnParams {
  const location = globalThis.location;
  if (location === undefined) return { code: null, state: null, error: null };
  const search = new URLSearchParams(location.search);
  const read = (key: string) => {
    const value = search.get(key);
    return value !== null && value.length > 0 ? value : null;
  };
  const params = { code: read('code'), state: read('state'), error: read('error') };
  if (location.search.length > 0) {
    try {
      globalThis.history?.replaceState(globalThis.history.state, '', location.pathname);
    } catch {
      // A browser that refuses: the parameters are single-use either way.
    }
  }
  return params;
}

type Outcome =
  | { kind: 'working' }
  | { kind: 'choose'; pending: OrgIntegrationPendingGrantOut }
  | { kind: 'refused'; message: string }
  | { kind: 'no-code' };

function connectedWords(
  result: OrgIntegrationAccountsCompleteOut,
  trip: ConnectTrip | null,
): string {
  const rows = result.connections ?? [];
  const name = rows[0]?.account_name?.trim() || w.unnamedAccount;
  if (trip?.reconnect && rows.length === 1) return w.reconnectedOne(name);
  return rows.length === 1 ? w.connectedOne(name) : w.connectedMany(rows.length);
}

export function ConnectReturnPage({ config }: { config: SocialConfig }) {
  const { orgRef } = config;
  const navigate = useNavigate();
  const refresh = useRefreshSocial(orgRef);
  const [params] = useState(takeReturnParams);
  const trip = orgRef === null ? null : readTrip(orgRef);
  const family = trip?.familyLabel ?? 'the platform';
  const [outcome, setOutcome] = useState<Outcome>(() =>
    params.error !== null
      ? { kind: 'refused', message: providerDeclinedWords(family) }
      : params.code !== null && params.state !== null
        ? { kind: 'working' }
        : { kind: 'no-code' },
  );
  const storedGrant = orgRef === null ? null : readPendingGrant(orgRef);
  const reloaded = usePendingGrant(
    outcome.kind === 'no-code' ? orgRef : null,
    outcome.kind === 'no-code' ? storedGrant : null,
  );
  const started = useRef(false);

  const finish = async (words: string) => {
    forgetTrip();
    toast.success(words);
    await refresh();
    void navigate({ to: trip?.returnPath ?? SOCIAL_ROUTES.accounts });
  };

  useEffect(() => {
    if (orgRef === null || started.current) return;
    const { code, state } = params;
    if (code === null || state === null || params.error !== null) return;
    started.current = true;
    if (SPENT_STATES.has(state)) return;
    SPENT_STATES.add(state);
    void (async () => {
      try {
        const { data } = await sdk.orgIntegrationsCompleteOrgIntegrationOauthAccounts({
          path: { org_ref: orgRef },
          body: { state, code, redirect_uri: socialRedirectUri() },
          throwOnError: true,
        });
        if (data.pending) {
          keepPendingGrant(orgRef, data.pending.grant_id);
          setOutcome({ kind: 'choose', pending: data.pending });
          return;
        }
        await finish(connectedWords(data, trip));
      } catch (error) {
        const code = isGivmoError(error) ? firstErrorCode(error) : null;
        setOutcome({ kind: 'refused', message: connectRefusalWords(code, family) });
      }
    })();
    // The params and the organization are read once: this page finishes one return.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgRef]);

  const back = () => {
    forgetTrip();
    void navigate({ to: trip?.returnPath ?? SOCIAL_ROUTES.accounts });
  };
  // Start the same connect again (the family, or the account being reconnected), from here.
  const [again, setAgain] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const tryAgain =
    orgRef !== null && trip?.start
      ? () => {
          const start = trip.start;
          if (!start) return;
          setStarting(true);
          setAgain(null);
          void startConnect(orgRef, start, undefined, trip.returnPath).then((refusal) => {
            if (refusal) {
              setStarting(false);
              setAgain(refusal);
            }
          });
        }
      : null;

  const pending =
    outcome.kind === 'choose'
      ? outcome.pending
      : outcome.kind === 'no-code' && reloaded.data
        ? reloaded.data
        : null;

  return (
    <SocialPageShell title={w.accountsTitle} testId={SOCIAL_PAGE_TEST_ID} width="narrow">
      {pending !== null && orgRef !== null ? (
        canManageConnections(config) || config.permissions === null ? (
          <AccountPicker
            orgRef={orgRef}
            orgName={config.orgName ?? null}
            pending={pending}
            family={family}
            onDone={(words) => void finish(words)}
            onAbandoned={() => {
              forgetTrip();
              toast(w.choiceAbandoned);
              void navigate({ to: trip?.returnPath ?? SOCIAL_ROUTES.accounts });
            }}
          />
        ) : null
      ) : outcome.kind === 'working' || (outcome.kind === 'no-code' && reloaded.isFetching) ? (
        <SocialLoading>{w.finishingTitle}</SocialLoading>
      ) : outcome.kind === 'refused' ? (
        <SocialStateCard
          testId="social-connect-refused"
          title={w.notConnectedTitle}
          description={again ?? outcome.message}
          action={
            <div className="flex flex-wrap items-center gap-8">
              {tryAgain ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  size="sm"
                  loading={starting}
                  onClick={tryAgain}
                  testId="social-connect-again"
                >
                  {w.tryConnectAgain}
                </Button>
              ) : null}
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={back}
                testId="social-connect-back"
              >
                {w.startAgain}
              </Button>
            </div>
          }
        />
      ) : (
        <SocialStateCard
          testId="social-connect-no-code"
          title={w.notConnectedTitle}
          description={w.noCodeBody}
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={back}
              testId="social-connect-back"
            >
              {w.startAgain}
            </Button>
          }
        />
      )}
    </SocialPageShell>
  );
}

function candidateKey(candidate: OrgIntegrationCandidateOut): string {
  return `${candidate.provider}:${candidate.subject}`;
}

/** Whole minutes left until a moment, at least one (the choice's deadline, said as a span). */
export function minutesLeft(iso: string, now: Date = new Date()): number {
  const left = new Date(iso).getTime() - now.getTime();
  return Number.isNaN(left) ? 15 : Math.max(1, Math.round(left / 60000));
}

function AccountPicker({
  orgRef,
  orgName,
  pending,
  family,
  onDone,
  onAbandoned,
}: {
  orgRef: string;
  orgName: string | null;
  pending: OrgIntegrationPendingGrantOut;
  family: string;
  onDone: (words: string) => void;
  onAbandoned: () => void;
}) {
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  // The organization's accounts already here: a candidate that is one of them says so (choosing it
  // again revives it in place; one that needs reconnecting is reconnected by choosing it).
  const connections = useConnections(orgRef);
  const existing = (candidate: OrgIntegrationCandidateOut) =>
    (connections.data ?? []).find(
      (row) =>
        row.provider === candidate.provider &&
        (row.account_name ?? '').trim() === candidate.name.trim(),
    ) ?? null;
  const meta = pending.candidates.some(
    (candidate) => candidate.provider === 'facebook_pages' || candidate.provider === 'instagram',
  );
  const [error, setError] = useState<string | null>(null);
  const select = useMutation({
    mutationFn: async () => {
      const choices = pending.candidates
        .filter((candidate) => chosen.has(candidateKey(candidate)))
        .map((candidate) => ({ provider: candidate.provider, subject: candidate.subject }));
      const { data } = await sdk.orgIntegrationsSelectOrgIntegrationPending({
        path: { org_ref: orgRef, grant_id: pending.grant_id },
        body: { choices },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => {
      const rows = data.connections ?? [];
      const name = rows[0]?.account_name?.trim() || w.unnamedAccount;
      onDone(rows.length === 1 ? w.connectedOne(name) : w.connectedMany(rows.length));
    },
    onError: (failure) => {
      const code = isGivmoError(failure) ? firstErrorCode(failure) : null;
      setError(connectRefusalWords(code, family));
    },
  });
  const abandon = useMutation({
    mutationFn: async () => {
      await sdk.orgIntegrationsAbandonOrgIntegrationPending({
        path: { org_ref: orgRef, grant_id: pending.grant_id },
        throwOnError: true,
      });
    },
    // Abandoned or already gone, nothing was connected either way.
    onSettled: onAbandoned,
  });
  const toggle = (key: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const busy = select.isPending || abandon.isPending;
  return (
    <Card className="flex flex-col gap-16 px-24 py-24" testId="social-account-picker">
      <div className="flex flex-col gap-4">
        <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
          {w.chooseTitle}
        </h2>
        <p className="font-sans text-s14 tracking-givmo text-text-secondary">
          {w.chooseBody(orgName)} {w.chooseExpires(minutesLeft(pending.expires_at))}
        </p>
      </div>
      <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle">
        {pending.candidates.map((candidate) => {
          const key = candidateKey(candidate);
          const id = `social-candidate-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
          return (
            <li key={key}>
              {/* The row is the label: its visible words name the box (no aria-label, which would
                  stack with them). */}
              <label className="flex cursor-pointer items-center gap-12 px-16 py-12">
                <Checkbox
                  appearance="saas"
                  checked={chosen.has(key)}
                  onCheckedChange={() => toggle(key)}
                  disabled={busy}
                  testId={id}
                />
                <PlatformMark platform={candidate.provider} size={24} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {candidate.name}
                  </span>
                  <span className="font-sans text-s13 tracking-givmo text-text-secondary">
                    {platformLabel(candidate.provider)}
                  </span>
                </span>
                {existing(candidate) ? (
                  <span
                    className={
                      existing(candidate)?.status === 'needs_reconnect'
                        ? 'shrink-0 font-sans text-s12 font-w500 tracking-givmo text-warning-700'
                        : 'shrink-0 font-sans text-s12 font-w500 tracking-givmo text-text-secondary'
                    }
                    {...testIdProp(`${id}-existing`)}
                  >
                    {existing(candidate)?.status === 'needs_reconnect'
                      ? w.reconnectsIt
                      : w.alreadyConnected}
                  </span>
                ) : null}
              </label>
            </li>
          );
        })}
      </ul>
      {meta ? (
        <p
          className="font-sans text-s13 tracking-givmo text-text-secondary"
          {...testIdProp('social-picker-instagram-hint')}
        >
          {w.instagramHint}
        </p>
      ) : null}
      {error ? <ErrorMessage testId="social-picker-error">{error}</ErrorMessage> : null}
      <div className="flex justify-end gap-8" {...testIdProp('social-picker-actions')}>
        <Button
          appearance="saas"
          tone="secondary"
          disabled={busy}
          loading={abandon.isPending}
          onClick={() => abandon.mutate()}
          testId="social-picker-cancel"
        >
          {w.chooseCancel}
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          disabled={busy || chosen.size === 0}
          loading={select.isPending}
          onClick={() => {
            setError(null);
            select.mutate();
          }}
          testId="social-picker-connect"
        >
          {w.chooseConnect}
        </Button>
      </div>
    </Card>
  );
}
